// Category Taxonomy — 15 clusters, 55+ subcategories, synonym resolution
// Used across UI, routing, scoring, analytics, personalization, repeat-buy

export interface CategoryCluster {
  id: string;
  label: string;
  subcategories: string[];
  repeatBuyPotential: "high" | "medium" | "low";
  defaultCycleDays?: number; // for repeat-buy eligible clusters
}

export const CATEGORY_TAXONOMY: CategoryCluster[] = [
  {
    id: "electronics",
    label: "Electronics & Tech",
    subcategories: ["monitors", "laptops", "tablets", "smartphones", "cameras", "gaming consoles", "gaming peripherals", "smart home", "networking", "webcams"],
    repeatBuyPotential: "low",
  },
  {
    id: "audio",
    label: "Audio",
    subcategories: ["headphones", "earbuds", "speakers", "soundbars"],
    repeatBuyPotential: "low",
  },
  {
    id: "home-appliances",
    label: "Home & Appliances",
    subcategories: ["vacuums", "robot vacuums", "air fryers", "dishwashers", "washers", "dryers", "microwaves", "air purifiers", "air conditioners"],
    repeatBuyPotential: "low",
  },
  {
    id: "furniture-office",
    label: "Furniture & Office",
    subcategories: ["office chairs", "desks", "standing desks", "bookshelves", "mattresses", "gaming chairs"],
    repeatBuyPotential: "low",
  },
  {
    id: "kitchen",
    label: "Kitchen",
    subcategories: ["espresso machines", "coffee makers", "blenders", "cookware", "knife sets", "toasters", "kettles"],
    repeatBuyPotential: "low",
  },
  {
    id: "beauty",
    label: "Beauty & Skincare",
    subcategories: ["skincare", "makeup", "hair care", "fragrance", "beauty tools", "skincare devices"],
    repeatBuyPotential: "high",
    defaultCycleDays: 45,
  },
  {
    id: "health",
    label: "Health & Supplements",
    subcategories: ["supplements", "vitamins", "protein powder", "pharmacy", "first aid", "fitness trackers", "smartwatches"],
    repeatBuyPotential: "high",
    defaultCycleDays: 30,
  },
  {
    id: "baby",
    label: "Baby & Kids",
    subcategories: ["strollers", "car seats", "baby monitors", "diapers", "toys", "baby gear"],
    repeatBuyPotential: "high",
    defaultCycleDays: 30,
  },
  {
    id: "pet",
    label: "Pet Products",
    subcategories: ["dog food", "cat food", "pet accessories", "pet health", "pet grooming"],
    repeatBuyPotential: "high",
    defaultCycleDays: 30,
  },
  {
    id: "groceries",
    label: "Groceries & Essentials",
    subcategories: ["household cleaners", "laundry", "paper goods", "pantry staples", "coffee", "tea"],
    repeatBuyPotential: "high",
    defaultCycleDays: 14,
  },
  {
    id: "fashion",
    label: "Fashion & Accessories",
    subcategories: ["shoes", "bags", "watches", "sunglasses", "basics", "jewelry"],
    repeatBuyPotential: "low",
  },
  {
    id: "tools",
    label: "Tools & DIY",
    subcategories: ["power tools", "hand tools", "garden tools", "tool storage"],
    repeatBuyPotential: "low",
  },
  {
    id: "sports",
    label: "Sports & Outdoor",
    subcategories: ["fitness equipment", "camping", "cycling", "running shoes", "yoga", "hiking"],
    repeatBuyPotential: "medium",
  },
  {
    id: "automotive",
    label: "Automotive",
    subcategories: ["car accessories", "dash cams", "car care", "car electronics"],
    repeatBuyPotential: "low",
  },
  {
    id: "travel",
    label: "Travel & Lifestyle",
    subcategories: ["luggage", "backpacks", "travel accessories", "portable chargers"],
    repeatBuyPotential: "low",
  },
];

// Synonym map: user input → canonical productType
export const CATEGORY_SYNONYMS: Record<string, string> = {
  // Beauty
  moisturizer: "skincare",
  serum: "skincare",
  cleanser: "skincare",
  sunscreen: "skincare",
  "face wash": "skincare",
  foundation: "makeup",
  lipstick: "makeup",
  mascara: "makeup",
  concealer: "makeup",
  shampoo: "hair care",
  conditioner: "hair care",
  "hair dryer": "beauty tools",
  "flat iron": "beauty tools",
  perfume: "fragrance",
  cologne: "fragrance",

  // Health
  "whey protein": "protein powder",
  creatine: "supplements",
  "fish oil": "supplements",
  multivitamin: "vitamins",
  "vitamin d": "vitamins",
  probiotic: "supplements",
  melatonin: "supplements",
  collagen: "supplements",
  "pre workout": "supplements",
  bcaa: "supplements",

  // Pet
  "dog food": "dog food",
  "cat food": "cat food",
  "cat litter": "pet accessories",
  "dog treats": "pet accessories",
  "pet bed": "pet accessories",
  leash: "pet accessories",
  "flea treatment": "pet health",

  // Baby
  stroller: "strollers",
  "baby stroller": "strollers",
  "car seat": "car seats",
  diaper: "diapers",
  nappies: "diapers",
  "baby monitor": "baby monitors",
  "baby gear": "baby gear",
  "baby formula": "baby gear",

  // Tools
  drill: "power tools",
  "impact driver": "power tools",
  "circular saw": "power tools",
  wrench: "hand tools",
  screwdriver: "hand tools",
  "lawn mower": "garden tools",
  trimmer: "garden tools",

  // Groceries
  "cleaning supplies": "household cleaners",
  detergent: "laundry",
  "paper towels": "paper goods",
  "toilet paper": "paper goods",
  "coffee beans": "coffee",
  "ground coffee": "coffee",

  // Sports
  treadmill: "fitness equipment",
  "exercise bike": "fitness equipment",
  "yoga mat": "yoga",
  dumbbells: "fitness equipment",
  "running shoes": "running shoes",
  "hiking boots": "hiking",
  tent: "camping",
  "sleeping bag": "camping",

  // Fashion
  sneakers: "shoes",
  trainers: "shoes",
  "dress shoes": "shoes",
  handbag: "bags",
  backpack: "backpacks",
  wallet: "bags",

  // Automotive
  "dash cam": "dash cams",
  "car charger": "car accessories",
  "floor mats": "car accessories",

  // Travel
  suitcase: "luggage",
  "carry on": "luggage",
  "power bank": "portable chargers",

  // Scale-Up: additional synonyms
  "face cream": "skincare",
  "anti-aging cream": "skincare",
  "eye cream": "skincare",
  "night cream": "skincare",
  "hair mask": "hair care",
  "dry shampoo": "hair care",
  "curling iron": "beauty tools",
  "lip gloss": "makeup",
  "setting spray": "makeup",
  "dog toys": "pet accessories",
  "cat tree": "pet accessories",
  "dog bed": "pet accessories",
  "cat toys": "pet accessories",
  "baby carrier": "baby gear",
  "high chair": "baby gear",
  "baby bottle": "baby gear",
  "socket set": "hand tools",
  hammer: "hand tools",
  pliers: "hand tools",
  "tape measure": "hand tools",
  "dish soap": "household cleaners",
  bleach: "household cleaners",
  "all-purpose cleaner": "household cleaners",
  "laundry detergent": "laundry",
  "fabric softener": "laundry",
  "dryer sheets": "laundry",
  "stain remover": "laundry",
};

// Resolve a user term to canonical productType
export function resolveCategory(term: string): { productType: string; clusterId: string } | null {
  const lower = term.toLowerCase().trim();

  // Check synonyms first
  const synonym = CATEGORY_SYNONYMS[lower];
  if (synonym) {
    const cluster = CATEGORY_TAXONOMY.find(c => c.subcategories.includes(synonym));
    if (cluster) return { productType: synonym, clusterId: cluster.id };
  }

  // Check direct subcategory match
  for (const cluster of CATEGORY_TAXONOMY) {
    for (const sub of cluster.subcategories) {
      if (lower.includes(sub) || sub.includes(lower)) {
        return { productType: sub, clusterId: cluster.id };
      }
    }
  }

  return null;
}

// Get repeat-buy cycle for a category
export function getRepeatBuyCycle(productType: string): { eligible: boolean; cycleDays: number } | null {
  for (const cluster of CATEGORY_TAXONOMY) {
    if (cluster.subcategories.includes(productType) && cluster.repeatBuyPotential === "high" && cluster.defaultCycleDays) {
      return { eligible: true, cycleDays: cluster.defaultCycleDays };
    }
  }
  return null;
}

// Get all subcategory count for stat validation
export function getTotalSubcategoryCount(): number {
  return CATEGORY_TAXONOMY.reduce((sum, c) => sum + c.subcategories.length, 0);
}

// Get cluster by subcategory
export function getClusterForSubcategory(productType: string): CategoryCluster | null {
  return CATEGORY_TAXONOMY.find(c => c.subcategories.includes(productType)) || null;
}
